import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { useBilling } from '../hooks/useBilling';
import ThemeEditorForm from '../components/themes/ThemeEditorForm';
import ThemePreviewPanel from '../components/themes/ThemePreviewPanel';
import type { InvoiceTheme, CreateInvoiceThemeDto, UpdateInvoiceThemeDto } from '../types/billing.types';

const DEFAULT_THEME: Partial<InvoiceTheme> = {
  name: '',
  description: '',
  is_default: false,
  primary_color: '#1a56db',
  secondary_color: '#6b7280',
  background_color: '#ffffff',
  content_background_color: '#ffffff',
  table_header_color: '#1a56db',
  table_row_color_1: '#ffffff',
  table_row_color_2: '#f9fafb',
  font_family: 'Arial, Helvetica, sans-serif',
  base_font_size: 10,
  header_font_size: 18,
  footer_font_size: 8,
  line_height: 1.4,
  page_size: 'A4',
  page_orientation: 'portrait',
  margin_top: 15,
  margin_bottom: 15,
  margin_left: 15,
  margin_right: 15,
  show_bill_number: true,
  show_issue_date: true,
  show_due_date: true,
  show_billing_period: true,
  show_client_information: true,
  show_matter_information: true,
  show_line_item_details: true,
  show_time_entry_details: false,
  show_expense_details: false,
  show_rates_and_hours: true,
  show_individual_amounts: true,
  show_subtotal: true,
  show_tax_breakdown: true,
  show_discounts: true,
  show_total_amount: true,
  show_amount_paid: true,
  show_balance_due: true,
  show_table_borders: true,
  alternate_row_colors: true,
  show_payment_instructions: false,
  include_qr_code: false,
  include_firm_registration: false,
  include_disclaimer: false,
  watermark_draft_bills: false,
  enable_pdf_export: true,
  enable_html_export: false,
};

const ThemeEditorPage: React.FC = () => {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useAnimatedNavigate();
  const { fetchThemeById, createTheme, updateTheme } = useBilling();
  const editingTheme = useBillingStore((s) => s.editingTheme);
  const setEditingTheme = useBillingStore((s) => s.setEditingTheme);
  const updateEditingThemeField = useBillingStore((s) => s.updateEditingThemeField);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loadingTheme, setLoadingTheme] = useState(false);

  const isNew = !themeId;

  // Load theme data
  useEffect(() => {
    if (themeId) {
      setLoadingTheme(true);
      fetchThemeById(themeId)
        .then((theme) => {
          setEditingTheme(theme);
        })
        .catch(() => {
          setSaveError('Failed to load theme');
        })
        .finally(() => setLoadingTheme(false));
    } else {
      setEditingTheme({ ...DEFAULT_THEME });
    }
    return () => {
      setEditingTheme(null);
    };
  }, [themeId]);

  const handleFieldChange = useCallback(
    (field: string, value: unknown) => {
      updateEditingThemeField(field, value);
    },
    [updateEditingThemeField]
  );

  const handleSave = async () => {
    if (!editingTheme) return;
    if (!editingTheme.name?.trim()) {
      setSaveError('Theme name is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (isNew) {
        await createTheme(editingTheme as CreateInvoiceThemeDto);
      } else {
        await updateTheme(themeId!, editingTheme as UpdateInvoiceThemeDto);
      }
      navigate('/billing/themes');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save theme');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/billing/themes');
  };

  if (loadingTheme) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading theme...</span>
      </div>
    );
  }

  if (!editingTheme) return null;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] lex-tab-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleCancel}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isNew ? 'Create Theme' : 'Edit Theme'}
            </h1>
            {editingTheme.name && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{editingTheme.name}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saveError && (
            <span className="text-xs text-red-500 mr-2">{saveError}</span>
          )}
          <button
            onClick={handleCancel}
            className="px-4 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : isNew ? 'Create Theme' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* 60/40 split layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — Form (60%) */}
        <div className="w-3/5 overflow-y-auto border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div className="p-6">
            <ThemeEditorForm theme={editingTheme} onChange={handleFieldChange} />
          </div>
        </div>

        {/* Right panel — Preview (40%) */}
        <div className="w-2/5 overflow-hidden bg-gray-100 dark:bg-gray-900">
          <ThemePreviewPanel theme={editingTheme} />
        </div>
      </div>
    </div>
  );
};

export default ThemeEditorPage;
